import { LoadingIndicator } from '../LoadingIndicator';
import {
  type InputProps,
  type InputWrapperProps,
  Input as MInput,
} from '@mantine/core';
import { useDebouncedValue, useDidUpdate } from '@mantine/hooks';
import {
  type ComponentPropsWithRef,
  forwardRef,
  useEffect,
  useState,
} from 'react';

type Props = {
  loadingQuery?: boolean;
  loadingMutation?: boolean;
  setValue?: (v: string) => void;
  debounce?: number;
} & Omit<InputProps, 'onChange'> &
  ComponentPropsWithRef<'input'>;

export const InputWrapper = forwardRef<HTMLInputElement, InputWrapperProps>(
  ({ styles, sx, ...props }, ref) => (
    <MInput.Wrapper
      inputWrapperOrder={['label', 'input', 'description', 'error']}
      {...props}
      ref={ref}
      styles={(theme, u, o) => ({
        error: {
          paddingTop: 4,
        },
        description: {
          paddingTop: 4,
        },
        ...(typeof styles === 'function' ? styles(theme, u, o) : styles),
      })}
      sx={(theme, u) => ({
        '.mantine-Input-wrapper': {
          marginBottom: 0,
        },
        ...((typeof sx === 'function' ? sx(theme, u) : sx) as any),
      })}
    >
      {props.children}
    </MInput.Wrapper>
  )
);
export const InputError = MInput.Error;

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      setValue,
      placeholder,
      loadingMutation,
      loadingQuery,
      debounce = 500,
      leftSection,
      value,
      sx,
      ...props
    },
    ref
  ) => {
    const hasOnchange = !!props.onChange;

    // The unsaved text, tied to the prop value it was typed over. A newer prop
    // value replaces it during render. Rows that were never typed in have no
    // draft, so a loaded value costs them no extra commit (mirroring the prop
    // into state did, and tripped React's nested update limit on long lists).
    const [draft, setDraft] = useState<{ text: string; base: typeof value }>();
    const [delayed] = useDebouncedValue(draft?.text, debounce);

    useDidUpdate(() => {
      if (typeof delayed === 'string' && setValue) {
        setValue(delayed);
      }
    }, [delayed]);

    const input = draft && draft.base === value ? draft.text : value;

    // Drop an outdated draft, so it cannot come back when the prop returns to
    // the value the draft was typed over.
    useEffect(() => {
      if (draft && draft.base !== value) setDraft(undefined);
    }, [draft, value]);

    return (
      <MInput
        ref={ref}
        value={hasOnchange ? value : input}
        onChange={(event) =>
          setDraft({ text: event.target.value, base: value })
        }
        sx={(theme, u) => ({
          height: '100%',
          width: props.width,
          input: {
            '&:focus': {
              boxShadow: `0px 0px 0px 2px ${
                theme.colors[props.error ? 'red' : theme.primaryColor][4]
              }40`,
              borderColor: props.error
                ? theme.colors.red[6]
                : theme.colors[theme.primaryColor][6],
            },
            '&:disabled': { color: 'unset' },
          },
          ".mantine-Input-section[data-position='right']": {
            pointerEvents: 'initial',
          },
          ...(typeof sx === 'function' ? (sx(theme, u) as any) : sx),
        })}
        radius="md"
        placeholder={placeholder}
        leftSection={
          loadingQuery ? <LoadingIndicator size="xs" /> : leftSection
        }
        rightSection={loadingMutation && <LoadingIndicator size="xs" />}
        name={props.name}
        {...props}
      />
    );
  }
);
